import React, { useState } from 'react';
import moment from 'moment';
import { Dropdown, Menu } from 'antd';
import masked from '~/common/masked';
import { Add, CaretDown } from '~/ui/assets/icons';
import { LOAN_TYPE_NAMES } from '~/typings/enums/LoanType';
import { OPERATION_STEPS_TYPES } from '~/typings/enums/OperationStepType';
import { Shimmer } from '~/ui/components';
import {
  BackButton,
  CardWrapper,
  ContractDropdownTrigger,
  ContractNumber,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  Header,
  HeaderLeft,
  MoreDetailsLink,
  MoreDetailsRow,
  OperationStepValue,
  ProductTag,
} from './ContractSummaryCard.styled';

const { toMoney } = masked;

export type ContractSummaryCardProps = {
  contractId: string;
  contractIds: string[];
  contractAuxIds?: Record<string, number>;
  loading: boolean;
  contractDetails: any;
  onBack?: () => void;
  onContractChange: (id: string) => void;
  onMoreDetails?: () => void;
};

export function ContractSummaryCard({
  contractId,
  contractIds,
  contractAuxIds,
  loading,
  contractDetails,
  onBack,
  onContractChange,
  onMoreDetails,
}: ContractSummaryCardProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const netValue = contractDetails?.netValue != null ? toMoney(contractDetails.netValue) : '-';
  const installments =
    contractDetails?.numberOfInstallments && contractDetails?.installmentValue
      ? `${contractDetails.numberOfInstallments}× ${toMoney(contractDetails.installmentValue)}`
      : '-';
  const contractDate = contractDetails?.contractDate
    ? moment(contractDetails.contractDate).format('DD/MM/YYYY - HH:mm')
    : '-';
  const outstandingBalance =
    contractDetails?.outstandingBalance != null ? toMoney(contractDetails.outstandingBalance) : '-';
  const retorno = contractDetails?.consigneeReturnDate
    ? moment(contractDetails.consigneeReturnDate).format('DD/MM/YYYY [às] HH:mm')
    : '-';

  const operationStep = contractDetails?.operationStep
    ? OPERATION_STEPS_TYPES[contractDetails.operationStep] || contractDetails.operationStep
    : '-';
  const product = contractDetails?.loanType
    ? LOAN_TYPE_NAMES[contractDetails.loanType] || contractDetails.loanType
    : null;
  const ccb = contractDetails?.ccbNumber || '-';

  return (
    <CardWrapper>
      <Header>
        <HeaderLeft>
          <ContractNumber>
            Contrato nº <strong>{contractId}</strong>
          </ContractNumber>
          {contractIds.length > 1 && (
            <Dropdown
              trigger={['click']}
              onVisibleChange={setDropdownOpen}
              overlay={
                <Menu selectedKeys={[contractId]} onClick={({ key }) => onContractChange(key as string)}>
                  {contractIds.map((id) => (
                    <Menu.Item key={id}>{contractAuxIds?.[id] ? `${id} (${contractAuxIds[id]})` : id}</Menu.Item>
                  ))}
                </Menu>
              }
            >
              <ContractDropdownTrigger type="button" title="Trocar contrato" $isOpen={dropdownOpen}>
                <CaretDown />
              </ContractDropdownTrigger>
            </Dropdown>
          )}
        </HeaderLeft>
        {onBack && (
          <BackButton type="button" onClick={onBack}>
            ← Voltar
          </BackButton>
        )}
      </Header>

      <FieldsGrid>
        <FieldGroup>
          <FieldLabel>Passo da esteira</FieldLabel>
          {loading ? (
            <Shimmer br={4} height={16} />
          ) : (
            <OperationStepValue as="span">{operationStep}</OperationStepValue>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Produto</FieldLabel>
          {loading && <Shimmer br={4} height={16} />}
          {!loading && product && <ProductTag>{product}</ProductTag>}
          {!loading && !product && <FieldValue>-</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Valor líquido</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{netValue}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Parcelas</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{installments}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Data/hora inicial</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{contractDate}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Saldo devedor</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{outstandingBalance}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Retorno</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{retorno}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>CCB</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{ccb}</FieldValue>}
        </FieldGroup>
      </FieldsGrid>

      {onMoreDetails && (
        <MoreDetailsRow>
          <MoreDetailsLink type="button" onClick={onMoreDetails}>
            <Add />
            Detalhes do contrato
          </MoreDetailsLink>
        </MoreDetailsRow>
      )}
    </CardWrapper>
  );
}
